import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BookMarked, Search } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import Navigation from '../components/Navigation';
import AdBanner from '../components/AdBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [chapters, setChapters] = useState([]);
  const [filteredChapters, setFilteredChapters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    fetchChapters();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = chapters.filter(
        (ch) =>
          ch.name_simple.toLowerCase().includes(searchQuery.toLowerCase()) ||
          ch.name_arabic.includes(searchQuery) ||
          ch.id.toString() === searchQuery
      );
      setFilteredChapters(filtered);
    } else {
      setFilteredChapters(chapters);
    }
  }, [searchQuery, chapters]);

  const fetchChapters = async () => {
    try {
      const response = await axios.get(`${API}/chapters`);
      setChapters(response.data);
      setFilteredChapters(response.data);
    } catch (error) {
      console.error('Error fetching chapters:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-32">
      {/* Navigation Menu */}
      <Navigation chapters={chapters} onNavigate={(surahId) => navigate(`/surah/${surahId}`)} />

      {/* Hero Section */}
      <div className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 hero-bg"></div>
        <div className="relative max-w-4xl mx-auto px-6">
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl sm:text-6xl font-light tracking-tight mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="hero-title"
          >
            {t('heroTitle')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-[#6B726B] leading-relaxed mb-8"
            data-testid="hero-subtitle"
          >
            {t('heroSubtitle')}
          </motion.p>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 text-[#6B726B]" size={24} strokeWidth={1.5} />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent border-0 border-b border-[#E8EAE8]/50 text-2xl py-4 pl-12 pr-4 search-input placeholder:text-[#6B726B]"
              data-testid="search-input"
            />
          </motion.div>

          {/* Bookmarks Link */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            onClick={() => navigate('/bookmarks')}
            className="mt-6 flex items-center gap-2 text-[#3A5A40] hover:text-[#2B4330] font-medium"
            data-testid="bookmarks-link"
          >
            <BookMarked size={20} strokeWidth={1.5} />
            {t('viewBookmarks')}
          </motion.button>
        </div>
      </div>

      {/* Surah List */}
      <div className="max-w-4xl mx-auto px-6">
        {loading ? (
          <div className="text-center py-12 text-[#6B726B]">Loading...</div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="surah-list"
            >
              {filteredChapters.map((chapter, index) => (
                <motion.div
                  key={chapter.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * index }}
                  onClick={() => navigate(`/surah/${chapter.id}`)}
                  className="surah-card bg-white border border-[#E8EAE8] rounded-2xl p-6 cursor-pointer"
                  data-testid={`surah-card-${chapter.id}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="text-sm text-[#A3B18A] font-medium mb-1" style={{ letterSpacing: '0.2em' }}>
                        {t(chapter.revelation_place.toLowerCase())}
                      </div>
                      <h3 className="text-xl font-medium mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {chapter.name_simple}
                      </h3>
                      <div className="text-sm text-[#6B726B]">{chapter.verses_count} {t('ayahs')}</div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="w-10 h-10 rounded-full bg-[#F4F5F4] flex items-center justify-center text-[#D4A373] font-medium">
                        {chapter.id}
                      </div>
                      <div className="text-2xl arabic-text" style={{ fontFamily: 'Amiri, serif' }}>
                        {chapter.name_arabic}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Ad Banner at bottom of surah list only */}
            <AdBanner adSlot="8447535141" className="mt-8" />
          </>
        )}
      </div>
    </div>
  );
};

export default Home;